import cookie from "js-cookie";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { newLivePublished, updateEndLive } from "../../redux/lives/livesSlice";
import { addLiveComment, newLiveTip, updateStreamStats } from "../../redux/lives/liveDataSlice";
import {
  addConversationToUnread,
  addMessage,
  clearAllConversations,
  createOrUpdateConversation,
  getConversationById,
  getUserConversations,
  handleConversationTypeChange,
  handleEditMessage,
  handleEditMessageStatus,
  handleNextUndeliveredMessageChange,
  markMessagesAsRead,
  readAllMessages,
  readAllMessagesInConversation,
  removeDeletedMessage,
  updateConversations,
  updateUnreadConversations,
  updateUnreadTypesConditional,
} from "../../redux/messages/messagesSlice";
import { AppDispatch, StoreState } from "../../redux/store";
import { authService, messageService } from "../../services";
import { getSocket, offSocketReconnected, onSocketReconnected } from "../../socket";
import { C_CURRENT_TAB, CONVERSATION_TYPES } from "../constant";
import { IConversations, IMessage, ISwitchConversationType, ISwitchUndeliveredMessage } from "../types";

type DeleteMessageSocketPayload = {
  messageId: string;
  conversationId: string;
  deletedBy?: string;
  deletedAt?: string;
  hasNewMessages?: boolean;
  lastMessage?: {
    sentBy: string;
    text?: string;
    isAudio?: boolean;
  };
};

export const MessagesInterceptor = () => {
  const dispatch = useDispatch<AppDispatch>();
  const shouldFetch = useRef(true);
  const { selectedConversation } = useSelector((state: StoreState) => state.messages);

  useEffect(() => {
    if (authService.isAuthenticated() && shouldFetch.current) {
      const role = authService.getRole();
      if (role === "creator" && !authService.isCreatorRegistrationComplete()) {
        return;
      }

      shouldFetch.current = false;
      messageService
        .getTotalNotReadMessages()
        .then((total) => {
          if (total && total !== "") {
            dispatch(updateUnreadConversations(parseInt(total, 10)));
          }
        })
        .catch((error) => {
          console.log("Error fetching unread messages:", error);
        });
    }
  }, []);

  useEffect(() => {
    const registerListeners = () => {
      const socket = getSocket();
      //console.log("🟡 Registering socket listeners on socket ID:", socket.id);

      // Remove existing listeners first to avoid duplicates
      socket.off("mediaPushHandler", mediaPushHandler);
      socket.off("mark-all-messages-read", handleMarkAllMessagesRead);
      socket.off("conversation-created", handleConversationCreated);
      socket.off("update-message", handleUpdateMessage);
      socket.off("switch-conversation-type", handleSwitchConversationType);
      socket.off("switch-next-message-to-deliver", handleSwitchToNextUndeliveredMessage);
      socket.off("streams_updated", streamLiveUpdated);
      socket.off("stream_state", handleStreamState);
      socket.off("mediaPush-sent", handleConversationsAfterMediaPush);
      socket.off("notify-read-messages-in-conversation-v1", handleUpdateUnreadConversations);
      socket.off("delete-message", handleDeleteMessage);
      socket.off("logout-all-devices", handleDisconnect);
      socket.off("passwordChanged", handleDisconnect);
      socket.off("disconnect-device", handleDisconnectDevice);
      socket.off("token:refresh", handleRefreshTokenMechanism);
      socket.off("token_expired");
      // Register listeners
      socket.on("mediaPushHandler", mediaPushHandler);
      socket.on("mark-all-messages-read", handleMarkAllMessagesRead);
      socket.on("conversation-created", handleConversationCreated);
      socket.on("update-message", handleUpdateMessage);
      socket.on("switch-conversation-type", handleSwitchConversationType);
      socket.on("switch-next-message-to-deliver", handleSwitchToNextUndeliveredMessage);
      socket.on("streams_updated", streamLiveUpdated);
      socket.on("stream_state", handleStreamState);
      socket.on("mediaPush-sent", handleConversationsAfterMediaPush);
      socket.on("notify-read-messages-in-conversation-v1", handleUpdateUnreadConversations);
      socket.on("delete-message", handleDeleteMessage);
      socket.on("logout-all-devices", handleDisconnect);
      socket.on("passwordChanged", handleDisconnect);
      socket.on("disconnect-device", handleDisconnectDevice);
      socket.on("token:refresh", handleRefreshTokenMechanism);
      socket.on("token_expired", async () => {
        const s = getSocket();
        s.auth = { token: authService.getToken("token") };
        s.disconnect().connect();
      });

      //console.log("🟢 All socket listeners registered successfully");
    };
    const unregisterListeners = () => {
      const socket = getSocket();
      socket.off("mediaPushHandler", mediaPushHandler);
      socket.off("mark-all-messages-read", handleMarkAllMessagesRead);
      socket.off("conversation-created", handleConversationCreated);
      socket.off("update-message", handleUpdateMessage);
      socket.off("switch-conversation-type", handleSwitchConversationType);
      socket.off("switch-next-message-to-deliver", handleSwitchToNextUndeliveredMessage);
      socket.off("streams_updated", streamLiveUpdated);
      socket.off("stream_state", handleStreamState);
      socket.off("mediaPush-sent", handleConversationsAfterMediaPush);
      socket.off("notify-read-messages-in-conversation-v1", handleUpdateUnreadConversations);
      socket.off("delete-message", handleDeleteMessage);
      socket.off("logout-all-devices", handleDisconnect);
      socket.off("passwordChanged", handleDisconnect);
      socket.off("disconnect-device", handleDisconnectDevice);
      socket.off("token:refresh", handleRefreshTokenMechanism);
      socket.off("token_expired");
    };
    // Handler for socket reconnection
    const handleSocketReconnected = (event: CustomEvent) => {
      //console.log("🔄 Socket reconnected, re-registering listeners for socket ID:", event.detail.socketId);
      registerListeners();
    };
    // Register listeners initially
    registerListeners();
    // Listen for socket reconnection events
    onSocketReconnected(handleSocketReconnected);
    return () => {
      unregisterListeners();
      offSocketReconnected(handleSocketReconnected);
    };
  }, []);

  useEffect(() => {
    const socket = getSocket();
    socket.on("conversation-updated", handleConversationUpdated);

    return () => {
      socket.off("conversation-updated", handleConversationUpdated);
    };
  }, [selectedConversation]);

  function mediaPushHandler(data: any) {
    dispatch(addMessage(data));
    dispatch(createOrUpdateConversation(data));
    if (data.conversation.hasNewMessages) dispatch(updateUnreadTypesConditional(data));
    if (data.messageStatus) {
      const messageData = { conversationId: data.conversation._id, _id: data.messageStatus._id, status: data.messageStatus.status };
      dispatch(handleEditMessageStatus(messageData));
    }
    //if unread is Opened
    if (authService.getId() !== data.message.senderId && data.conversation.hasNewMessages) {
      dispatch(addConversationToUnread(data));
      // dispatch(updateUnreadConversations(data.totalUnread));
    }
    // dispatch(updateConversations(data));
  }

  function handleMarkAllMessagesRead(data: any) {
    if (data && data.conversationId) {
      if (data?.creatorId === authService.getId()) {
        dispatch(readAllMessagesInConversation(data));
      }
      if (data.total != undefined) dispatch(updateUnreadConversations(data.total));

      dispatch(markMessagesAsRead({ conversationId: data.conversationId, userId: data?.creatorId }));
    }
  }

  function handleConversationCreated(data: IConversations) {
    dispatch(updateConversations(data));
  }

  function handleUpdateMessage(data: IMessage) {
    dispatch(handleEditMessage(data));
  }

  function handleSwitchConversationType(data: ISwitchConversationType) {
    dispatch(handleConversationTypeChange(data));
  }

  function handleSwitchToNextUndeliveredMessage(data: ISwitchUndeliveredMessage) {
    dispatch(handleNextUndeliveredMessageChange(data));
  }

  function handleConversationUpdated(data: IConversations) {
    if (data && selectedConversation) {
      if (
        (window.location.pathname.startsWith("/fan/requestMedia/") || window.location.pathname.startsWith("/creator/conversation/")) &&
        data._id === selectedConversation._id
      ) {
        dispatch(readAllMessages(selectedConversation._id));
      }
    }
    if (data) {
      dispatch({
        type: getConversationById.fulfilled.type,
        payload: data,
      });
    }
  }

  const streamLiveUpdated = (data: any) => {
    if (data?.isNewStream && data?.stream) {
      dispatch(newLivePublished(true));
    }
    if (data?.isStreamingEnds && data?.streamId) {
      dispatch(updateEndLive({ streamId: data?.streamId, isStreamingEnds: data?.isStreamingEnds }));
    }
  };

  const handleStreamState = (data: any) => {
    if (data?.comments) {
      dispatch(addLiveComment(data.comments));
    }
    if (data?.lastStreamTip) {
      dispatch(newLiveTip(data.lastStreamTip));
    }
    if (data?.streamStats) {
      dispatch(updateStreamStats(data.streamStats));
    }
  };

  const handleConversationsAfterMediaPush = (data: any) => {
    dispatch(clearAllConversations());
    localStorage.removeItem("mediaPushSentTime");
    window.dispatchEvent(
      new CustomEvent("localStorageChange", {
        detail: { key: "mediaPushSentTime", newValue: null },
      })
    );
    dispatch(getUserConversations({ page: 1, type: window.localStorage.getItem(C_CURRENT_TAB) || CONVERSATION_TYPES.GENERAL }));
  };

  const handleUpdateUnreadConversations = (data: { creatorId: string; total: number }) => {
    dispatch(updateUnreadConversations(data.total));
  };

  const handleDeleteMessage = (data: DeleteMessageSocketPayload) => {
    if (!data?.messageId || !data?.conversationId) return;

    dispatch(removeDeletedMessage(data));
  };

  const handleDisconnect = (data: any) => {
    if ((data.type = "app")) {
      cookie.remove("token");
      cookie.remove("refreshToken");

      localStorage.removeItem("hasVideoPlayed");
      localStorage.removeItem("deviceId");

      window.location.href = "/auth/signIn";
    }
  };

  const handleDisconnectDevice = (data: any) => {
    if (localStorage.getItem("deviceId") === data.deviceId) {
      handleDisconnect({ type: "app" });
    }
  };

  const handleRefreshTokenMechanism = (data: any) => {
    authService.setToken(data.token);
    authService.setRefreshToken(data.refreshToken);
    localStorage.setItem("deviceId", data.deviceId);
  };
  return <></>;
};
